import type { IFieldOption, IModuleField } from '@/features/module-builder/types';

/**
 * Plain-text formatting of snapshot values for table cells, kanban cards, and
 * fallbacks. Rich rendering (badges, stars, avatars) lives in the shared
 * `RecordFieldValue` component — this is the text layer both build on.
 */

export function getOption(field: IModuleField, value: unknown): IFieldOption | undefined {
    return (field.options ?? []).find((o) => o.value === String(value));
}

export function formatFieldValue(field: IModuleField, value: unknown): string {
    if (value === null || value === undefined || value === '') return '';

    switch (field.fieldType) {
        case 'BOOLEAN':
            return value === true || value === 'true' ? '✓' : '✕';
        case 'SELECT':
            return getOption(field, value)?.label ?? String(value);
        case 'MULTI_SELECT': {
            const items = Array.isArray(value) ? value : [value];
            return items.map((v) => getOption(field, v)?.label ?? String(v)).join(', ');
        }
        case 'NUMBER': {
            const num = Number(value);
            if (!Number.isFinite(num)) return String(value);
            const decimals = field.settings?.decimalPlaces;
            return decimals === undefined
                ? num.toLocaleString()
                : num.toLocaleString(undefined, {
                      minimumFractionDigits: decimals,
                      maximumFractionDigits: decimals,
                  });
        }
        case 'CURRENCY': {
            const num = Number(value);
            if (!Number.isFinite(num)) return String(value);
            const symbol = field.settings?.currencySymbol ?? '$';
            const decimals = field.settings?.decimalPlaces ?? 2;
            return `${symbol}${num.toLocaleString(undefined, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            })}`;
        }
        case 'PERCENTAGE': {
            const num = Number(value);
            return Number.isFinite(num) ? `${num.toLocaleString()}%` : String(value);
        }
        case 'RATING': {
            const num = Number(value);
            const scale = field.settings?.scale ?? 5;
            return Number.isFinite(num) ? `${num}/${scale}` : String(value);
        }
        case 'DATE': {
            const date = new Date(String(value));
            return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
        }
        case 'DATETIME': {
            const date = new Date(String(value));
            return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
        }
        case 'AUTO_INCREMENT': {
            const prefix = field.settings?.prefix ?? '';
            const digits = field.settings?.digits;
            const num = Number(value);
            if (!Number.isFinite(num)) return String(value);
            const padded = digits ? String(num).padStart(digits, '0') : String(num);
            return `${prefix}${padded}`;
        }
        case 'LOOKUP':
        case 'USER':
            // Ids are resolved to display names by the rich renderer.
            return Array.isArray(value) ? value.map(String).join(', ') : String(value);
        default:
            return String(value);
    }
}
